import type {
  ContentBlock,
  ContentBlockBannerItemsItem,
  ContentBlockBannerItemsItemCtasItem,
  ContentBlockBannerItemsItemMedia,
  ContentSection,
  PatchAdminV1StoresStoreIdBlocksIdBody,
  PatchAdminV1StoresStoreIdSectionsIdBody,
  PostAdminV1StoresStoreIdBlocksBody,
  PostAdminV1StoresStoreIdBlocksPreviewBody,
  PostAdminV1StoresStoreIdSectionsBody,
} from '@nucleus-commerce/api-client/admin';

import {
  RAIL_LIMIT_DEFAULT,
  RAIL_LIMIT_MAX,
  type BannerFormInput,
  type BannerFormOutput,
  type BannerItemForm,
  type BlockFormInput,
  type BlockFormOutput,
  type BlockScope,
  type CtaForm,
  type CustomEntryForm,
  type MediaRefForm,
  type RailFormInput,
  type RailFormOutput,
  type RailHeaderForm,
} from '../schemas/block-form-schema';
import type { SectionFormInput, SectionFormOutput } from '../schemas/section-form-schema';

type ApiBannerItem = Extract<
  PostAdminV1StoresStoreIdBlocksBody,
  { kind: 'banner' }
>['banner']['items'][number];
type ApiCta = NonNullable<ApiBannerItem['ctas']>[number];
type ApiMediaRef = NonNullable<ApiBannerItem['media']>;
type CustomBag = Record<string, unknown>;

/** What both preview routes take: content only, lenient, so a half-filled form still previews. */
export type PreviewBlockBody = PostAdminV1StoresStoreIdBlocksPreviewBody;
export type PreviewBannerItem = Extract<
  PreviewBlockBody,
  { kind: 'banner' }
>['banner']['items'][number];
export type PreviewCta = NonNullable<PreviewBannerItem['ctas']>[number];
export type PreviewMediaRef = NonNullable<PreviewBannerItem['media']>;

const orNull = (value: string): string | null => (value === '' ? null : value);

/** ISO from the API → the value a datetime-local input wants (local time, minute precision). */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromLocalInput(local: string): string | null {
  if (local === '') return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function customToBag(entries: readonly CustomEntryForm[]): CustomBag {
  const bag: CustomBag = {};
  for (const entry of entries) {
    switch (entry.type) {
      case 'number':
        bag[entry.key] = Number(entry.value);
        break;
      case 'boolean':
        bag[entry.key] = entry.value === 'true';
        break;
      case 'json':
        bag[entry.key] = JSON.parse(entry.value) as unknown;
        break;
      case 'string':
        bag[entry.key] = entry.value;
    }
  }
  return bag;
}

export function bagToCustom(bag: CustomBag | null | undefined): CustomEntryForm[] {
  return Object.entries(bag ?? {}).map(([key, value]) => {
    if (typeof value === 'number') return { key, type: 'number', value: String(value) };
    if (typeof value === 'boolean')
      return { key, type: 'boolean', value: value ? 'true' : 'false' };
    if (typeof value === 'string') return { key, type: 'string', value };
    return { key, type: 'json', value: JSON.stringify(value) };
  });
}

export const emptyMediaUrl = (): MediaRefForm => ({
  type: 'url',
  url: '',
  kind: 'image',
  alt: '',
  width: '',
  height: '',
});

export const emptyCta = (): CtaForm => ({
  label: '',
  linkType: 'url',
  url: '',
  productId: '',
  productTitle: '',
  collectionId: '',
  openInNewTab: false,
  style: '',
});

export const emptyBannerItem = (): BannerItemForm => ({
  media: null,
  mobileMedia: null,
  eyebrow: '',
  title: '',
  subtitle: '',
  descriptionHtml: '',
  ctas: [],
  custom: [],
});

/** A header with nothing in it is not a header; the mapper sends null and the editor shows "Add a header". */
export function bannerItemIsEmpty(item: RailHeaderForm): boolean {
  return (
    item.media === null &&
    item.mobileMedia === null &&
    item.eyebrow === '' &&
    item.title === '' &&
    item.subtitle === '' &&
    item.descriptionHtml === '' &&
    item.ctas.length === 0 &&
    item.custom.length === 0
  );
}

const emptyCommon = () => ({
  key: '',
  name: '',
  status: 'draft' as const,
  startsAt: '',
  endsAt: '',
  custom: [],
});

/** A new banner starts with one slide so the editor never opens on an empty list. */
export function emptyBannerForm(): BannerFormInput {
  return { kind: 'banner', ...emptyCommon(), items: [emptyBannerItem()] };
}

/** A shared rail can only be hand-picked (collections belong to a store), so it starts there. */
export function emptyRailForm(scope: BlockScope): RailFormInput {
  return {
    kind: 'rail',
    ...emptyCommon(),
    header: emptyBannerItem(),
    sourceType: scope === 'shared' ? 'products' : 'collection',
    collectionId: '',
    products: [],
    limit: String(RAIL_LIMIT_DEFAULT),
  };
}

function fromMediaRef(ref: ContentBlockBannerItemsItemMedia): MediaRefForm {
  if (!ref) return null;
  if (ref.type === 'library') return { type: 'library', mediaId: ref.mediaId, alt: ref.alt ?? '' };
  return {
    type: 'url',
    url: ref.url,
    kind: ref.kind,
    alt: ref.alt,
    width: ref.width === undefined ? '' : String(ref.width),
    height: ref.height === undefined ? '' : String(ref.height),
  };
}

function fromCta(cta: ContentBlockBannerItemsItemCtasItem): CtaForm {
  const base = {
    ...emptyCta(),
    label: cta.label,
    openInNewTab: cta.openInNewTab,
    style: cta.style ?? '',
  };
  switch (cta.link.type) {
    case 'url':
      return { ...base, linkType: 'url', url: cta.link.url };
    case 'product':
      return { ...base, linkType: 'product', productId: cta.link.productId };
    case 'collection':
      return { ...base, linkType: 'collection', collectionId: cta.link.collectionId };
  }
}

function fromItem(item: ContentBlockBannerItemsItem): BannerItemForm {
  return {
    media: fromMediaRef(item.media),
    mobileMedia: fromMediaRef(item.mobileMedia),
    eyebrow: item.eyebrow ?? '',
    title: item.title ?? '',
    subtitle: item.subtitle ?? '',
    descriptionHtml: item.descriptionHtml ?? '',
    ctas: item.ctas.map(fromCta),
    custom: bagToCustom(item.custom),
  };
}

function commonFromBlock(row: ContentBlock) {
  return {
    key: row.key,
    name: row.name,
    status: row.status,
    startsAt: toLocalInput(row.startsAt),
    endsAt: toLocalInput(row.endsAt),
    custom: bagToCustom(row.custom),
  };
}

/** The row's kind picks the branch; a rail row without `rail` (never sent by the API) falls back to an empty rail. */
export function fromBlock(row: ContentBlock): BlockFormInput {
  if (row.kind === 'banner') {
    return {
      kind: 'banner',
      ...commonFromBlock(row),
      items: row.banner ? row.banner.items.map(fromItem) : [emptyBannerItem()],
    };
  }
  const rail = row.rail;
  const empty = emptyRailForm(row.scope);
  if (!rail) return { ...empty, ...commonFromBlock(row) };
  return {
    kind: 'rail',
    ...commonFromBlock(row),
    header: rail.header ? fromItem(rail.header) : emptyBannerItem(),
    sourceType: rail.source.type,
    collectionId: rail.source.type === 'collection' ? (rail.source.collectionId ?? '') : '',
    products:
      rail.source.type === 'products'
        ? rail.source.products.map((p) => ({
            productId: p.productId,
            title: p.title,
            handle: p.handle,
            status: p.status,
          }))
        : [],
    limit: String(rail.limit),
  };
}

function toMediaRef(ref: MediaRefForm): ApiMediaRef | null {
  if (!ref) return null;
  if (ref.type === 'library') {
    return { type: 'library', mediaId: ref.mediaId, ...(ref.alt ? { alt: ref.alt } : {}) };
  }
  return {
    type: 'url',
    url: ref.url,
    kind: ref.kind,
    alt: ref.alt,
    ...(ref.width ? { width: Number(ref.width) } : {}),
    ...(ref.height ? { height: Number(ref.height) } : {}),
  };
}

function toCta(cta: CtaForm): ApiCta {
  const link: ApiCta['link'] =
    cta.linkType === 'url'
      ? { type: 'url', url: cta.url }
      : cta.linkType === 'product'
        ? { type: 'product', productId: cta.productId }
        : { type: 'collection', collectionId: cta.collectionId };
  return { label: cta.label, link, openInNewTab: cta.openInNewTab, style: orNull(cta.style) };
}

function toItem(item: RailHeaderForm): ApiBannerItem {
  return {
    media: toMediaRef(item.media),
    mobileMedia: toMediaRef(item.mobileMedia),
    eyebrow: orNull(item.eyebrow),
    title: orNull(item.title),
    subtitle: orNull(item.subtitle),
    descriptionHtml: orNull(item.descriptionHtml),
    ctas: item.ctas.map(toCta),
    custom: customToBag(item.custom),
  };
}

function bannerBody(values: BannerFormOutput) {
  return { items: values.items.map(toItem) };
}

function railBody(
  values: RailFormOutput,
): NonNullable<PatchAdminV1StoresStoreIdBlocksIdBody['rail']> {
  return {
    header: bannerItemIsEmpty(values.header) ? null : toItem(values.header),
    source:
      values.sourceType === 'collection'
        ? { type: 'collection', collectionId: values.collectionId }
        : { type: 'products', productIds: values.products.map((p) => p.productId) },
    limit: Number(values.limit),
  };
}

function scalars(values: BlockFormOutput) {
  return {
    key: values.key,
    name: values.name,
    status: values.status,
    startsAt: fromLocalInput(values.startsAt),
    endsAt: fromLocalInput(values.endsAt),
    custom: customToBag(values.custom),
  };
}

export function toBlockCreate(values: BlockFormOutput): PostAdminV1StoresStoreIdBlocksBody {
  return values.kind === 'banner'
    ? { kind: 'banner', ...scalars(values), banner: bannerBody(values) }
    : { kind: 'rail', ...scalars(values), rail: railBody(values) };
}

export function toBlockUpdate(values: BlockFormOutput): PatchAdminV1StoresStoreIdBlocksIdBody {
  return values.kind === 'banner'
    ? { ...scalars(values), banner: bannerBody(values) }
    : { ...scalars(values), rail: railBody(values) };
}

/** A host is required: `https://` alone is what the URL field starts with. */
const HTTPS_URL = /^https:\/\/[^\s/?#]+/;
const CTA_HREF = /^(https:\/\/[^\s/?#]+|\/)/;
const POSITIVE_INT = /^[1-9]\d*$/;

/** A media ref the resolver can look up, or null while the field is still half-filled. */
export function toPreviewMediaRef(ref: MediaRefForm | undefined): PreviewMediaRef | null {
  if (!ref) return null;
  if (ref.type === 'library') {
    if (ref.mediaId === '') return null;
    const alt = ref.alt.trim();
    return { type: 'library', mediaId: ref.mediaId, ...(alt ? { alt } : {}) };
  }
  const url = ref.url.trim();
  if (!HTTPS_URL.test(url)) return null;
  const alt = ref.alt.trim();
  return {
    type: 'url',
    url,
    kind: ref.kind,
    ...(alt ? { alt } : {}),
    ...(POSITIVE_INT.test(ref.width.trim()) ? { width: Number(ref.width) } : {}),
    ...(POSITIVE_INT.test(ref.height.trim()) ? { height: Number(ref.height) } : {}),
  };
}

/** A button with a label and a complete link; anything less is left out of the preview body. */
export function toPreviewCta(cta: CtaForm): PreviewCta | null {
  const label = cta.label.trim();
  if (label === '') return null;
  let link: PreviewCta['link'];
  if (cta.linkType === 'url') {
    const url = cta.url.trim();
    if (!CTA_HREF.test(url)) return null;
    link = { type: 'url', url };
  } else if (cta.linkType === 'product') {
    if (cta.productId === '') return null;
    link = { type: 'product', productId: cta.productId };
  } else {
    if (cta.collectionId === '') return null;
    link = { type: 'collection', collectionId: cta.collectionId };
  }
  return { label, link, openInNewTab: cta.openInNewTab, style: orNull(cta.style.trim()) };
}

/** Like customToBag, but rows without a key or with a value that does not parse are skipped. */
function previewBag(entries: readonly CustomEntryForm[]): CustomBag {
  const bag: CustomBag = {};
  for (const entry of entries) {
    const key = entry.key.trim();
    if (key === '') continue;
    if (entry.type === 'number') {
      const value = Number(entry.value);
      if (entry.value.trim() !== '' && !Number.isNaN(value)) bag[key] = value;
    } else if (entry.type === 'json') {
      try {
        bag[key] = JSON.parse(entry.value) as unknown;
      } catch {
        // Still being typed; the save path reports it.
      }
    } else {
      bag[key] = entry.type === 'boolean' ? entry.value === 'true' : entry.value;
    }
  }
  return bag;
}

function toPreviewItem(item: RailHeaderForm): PreviewBannerItem {
  return {
    media: toPreviewMediaRef(item.media),
    mobileMedia: toPreviewMediaRef(item.mobileMedia),
    eyebrow: orNull(item.eyebrow.trim()),
    title: orNull(item.title.trim()),
    subtitle: orNull(item.subtitle.trim()),
    descriptionHtml: orNull(item.descriptionHtml.trim()),
    ctas: item.ctas.flatMap((cta) => toPreviewCta(cta) ?? []),
    custom: previewBag(item.custom),
  };
}

/**
 * The editor's current values as a preview body. Unlike toBlockCreate it takes the raw form
 * input, never throws and never refuses: empty buttons, half-typed media and unparsable custom
 * rows are dropped, an invalid limit falls back to the server default, and slides are kept
 * one-to-one so the payload lines up with the form by position.
 */
export function toPreviewBody(values: BlockFormInput): PreviewBlockBody {
  const custom = previewBag(values.custom);
  if (values.kind === 'banner') {
    return { kind: 'banner', custom, banner: { items: values.items.map(toPreviewItem) } };
  }
  const limit = Number(values.limit.trim());
  return {
    kind: 'rail',
    custom,
    rail: {
      header: bannerItemIsEmpty(values.header) ? null : toPreviewItem(values.header),
      // No collection picked yet resolves like an empty list, so the header still previews.
      source:
        values.sourceType === 'collection' && values.collectionId !== ''
          ? { type: 'collection', collectionId: values.collectionId }
          : {
              type: 'products',
              productIds:
                values.sourceType === 'products' ? values.products.map((p) => p.productId) : [],
            },
      ...(POSITIVE_INT.test(values.limit.trim()) && limit <= RAIL_LIMIT_MAX ? { limit } : {}),
    },
  };
}

export function emptySectionForm(): SectionFormInput {
  return { handle: '', name: '', title: '', description: '', status: 'draft', custom: [] };
}

export function fromSection(row: ContentSection): SectionFormInput {
  return {
    handle: row.handle,
    name: row.name,
    title: row.title ?? '',
    description: row.description ?? '',
    status: row.status,
    custom: bagToCustom(row.custom),
  };
}

export function toSectionCreate(values: SectionFormOutput): PostAdminV1StoresStoreIdSectionsBody {
  return {
    handle: values.handle,
    name: values.name,
    title: orNull(values.title),
    description: orNull(values.description),
    status: values.status,
    custom: customToBag(values.custom),
    blockIds: [],
  };
}

export function toSectionUpdate(
  values: SectionFormOutput,
): PatchAdminV1StoresStoreIdSectionsIdBody {
  return {
    handle: values.handle,
    name: values.name,
    title: orNull(values.title),
    description: orNull(values.description),
    status: values.status,
    custom: customToBag(values.custom),
  };
}
